import React, { useState, useRef, useEffect } from 'react';

const VoicesCarousel = ({ voices = [] }) => {
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingVoiceId, setLoadingVoiceId] = useState(null);
  const audioRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Generate color from voice name (deterministic hash to hsl)
  const getVoiceColor = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    // Use golden/warm hues (35-55) and accent hues to match design
    const adjustedHue = (hue % 3 === 0) ? 45 : (hue % 3 === 1) ? 35 : hue % 180;
    return `hsl(${adjustedHue}, 70%, 60%)`;
  };

  const handlePlayVoice = (e, voiceId, audioUrl) => {
    e.stopPropagation();

    // Toggle if same voice
    if (isPlaying && playingVoiceId === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        setPlayingVoiceId(null);
      }
      return;
    }

    // Stop current and play new
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setLoadingVoiceId(voiceId);
    setPlayingVoiceId(voiceId);
    setIsPlaying(true);

    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play()
        .then(() => setLoadingVoiceId(null))
        .catch((error) => {
          console.error('Error playing audio:', error);
          setIsPlaying(false);
          setPlayingVoiceId(null);
          setLoadingVoiceId(null);
        });
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayingVoiceId(null);
    setLoadingVoiceId(null);
  };

  // Duplicate voices for seamless infinite scroll
  const duplicatedVoices = [...voices, ...voices];

  return (
    <div className="voices-carousel-container">
      <div className="voices-carousel-track">
        {duplicatedVoices.map((voice, index) => {
          const isCurrentlyPlaying = playingVoiceId === voice.id && isPlaying;
          const isLoading = loadingVoiceId === voice.id;
          const iconColor = getVoiceColor(voice.name);

          return (
            <div
              key={`${voice.id}-${index}`}
              className="voice-card"
            >
              {/* Voice Icon */}
              <div
                className="voice-icon"
                style={{
                  background: `linear-gradient(135deg, ${iconColor}, ${iconColor}dd)`
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                </svg>
              </div>

              {/* Voice Info */}
              <div className="voice-info">
                <h4 className="voice-name">{voice.name}</h4>
                {voice.tags && voice.tags.length > 0 && (
                  <div className="voice-tags">
                    {voice.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="voice-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Play Button */}
              <button
                className={`voice-play-btn ${isCurrentlyPlaying ? 'playing' : ''} ${isLoading ? 'loading' : ''}`}
                onClick={(e) => handlePlayVoice(e, voice.id, voice.audio_url)}
                disabled={isLoading}
                aria-label={isCurrentlyPlaying ? 'Pause voice sample' : 'Play voice sample'}
              >
                {isLoading ? (
                  <span className="loading-spinner" />
                ) : isCurrentlyPlaying ? (
                  <span>⏸</span>
                ) : (
                  <span>▶</span>
                )}
              </button>

              {/* Card Border */}
              <div className="voice-card-border"></div>
            </div>
          );
        })}
      </div>

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        preload="none"
      />
    </div>
  );
};

export default VoicesCarousel;
